import { EditButton, WarningCard } from '@automata/ui';
import { useFlag } from '@automata/utils/hooks';
import { dateValueFormatter } from '@automata/utils/src/time';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { type FC, useCallback, useMemo } from 'react';
import { useToggle } from 'usehooks-ts';

import { EditExecutorOptionsModal } from './EditExecutorOptionsModal/EditExecutorOptionsModal';
import {
  selectDriversByInstrumentId,
  selectSelectedWorkcellId,
  selectSelectedWorkcellName,
  selectWorkcellInstruments,
  selectWorkcellTransportConfigId,
  selectWorkflowDescription,
  selectWorkflowMetadata,
} from '../../../features/workflow/selectors';
import {
  WorkcellInstrumentsById,
  WorkflowMetadata,
} from '../../../features/workflow/types';
import { setWorkflowDescription } from '../../../features/workflow/workflowSlice';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { useGetInstrumentsConfigState } from '../../../hooks/useGetInstrumentsConfigState';
import { useGetInstrumentsRenameMapping } from '../../../hooks/useGetInstrumentsRenameMapping';
import { useGetInstrumentsState } from '../../../hooks/useGetInstrumentsState';
import { useGetWorkcell } from '../../../hooks/useGetWorkcell';
import { SDK_INTEROPERABILITY_WORKCELL_ID } from '../../../hooks/useGetWorkcell/types';
import { prepareInstrumentsForWorkflowConfig } from '../../../utils';
import { SchedulerVersionSelector } from '../../SchedulerVersionSelector';
import { SemicontrolledInput } from '../../SemicontrolledInput';
import { toolbarStyles } from '../../styles';
import { WorkcellFormModal } from '../../WorkcellFormModal';
import { compareFormValues } from '../../WorkcellFormModal/utils';

export type WorkflowDescriptionPanelProps = {
  description: string;
  metadata?: WorkflowMetadata;
  onEditDescription: (description: string) => void;
};

const dividerStyles = { margin: '1rem 0' };

const displayOrDefault = (value?: string | null): string => {
  return value ?? '---';
};

const displayDateOrDefault = (date?: string | null): string => {
  return date ? dateValueFormatter(date, true) : '---';
};

const getMetadataFields = (metadata?: WorkflowMetadata) => [
  { label: 'CREATED BY:', value: displayOrDefault(metadata?.createdBy) },
  { label: 'CREATED:', value: displayDateOrDefault(metadata?.createdAt) },
  { label: 'UPDATED:', value: displayDateOrDefault(metadata?.updatedAt) },
  { label: 'PUBLISHED:', value: displayDateOrDefault(metadata?.publishedAt) },
];

export const WorkflowDescriptionPanel: FC<WorkflowDescriptionPanelProps> = ({
  description,
  metadata,
  onEditDescription,
}) => {
  const isWorkcellEditToolEnabled = useFlag('fe.workcell_edit_tool');
  const isExecutorOptionsEnabled = useFlag('fe.executor_options');
  const [showWorkcellFormModal, toggleWorkcellFormModal] = useToggle();
  const [showExecutorOptionsModal, toggleExecutorOptionsModal] = useToggle();

  const driversByInstrumentId = useAppSelector(selectDriversByInstrumentId);
  const selectedWorkcellId = useAppSelector(selectSelectedWorkcellId);
  const selectedWorkcellName = useAppSelector(selectSelectedWorkcellName);
  const selectedTransportConfigId = useAppSelector(
    selectWorkcellTransportConfigId
  );
  const initialConfig = useAppSelector(selectWorkcellInstruments);

  const isInteroperabilityWorkcell =
    selectedWorkcellId === SDK_INTEROPERABILITY_WORKCELL_ID;

  const { data: getWorkcellData, isLoading: isWorkcellLoading } =
    useGetWorkcell(selectedWorkcellId, {
      enabled: !isInteroperabilityWorkcell,
    });

  const instrumentStates = useGetInstrumentsState(selectedWorkcellId);
  const renameInstrumentsMapping =
    useGetInstrumentsRenameMapping(selectedWorkcellId);
  const instrumentConfigStates =
    useGetInstrumentsConfigState(selectedWorkcellId);

  const defaultWorkcellConfig = useMemo(() => {
    const preparedInstruments = prepareInstrumentsForWorkflowConfig(
      getWorkcellData?.workcellInstrumentsById ?? {},
      instrumentStates,
      renameInstrumentsMapping,
      instrumentConfigStates
    );

    return preparedInstruments as WorkcellInstrumentsById;
  }, [
    getWorkcellData?.workcellInstrumentsById,
    instrumentConfigStates,
    instrumentStates,
    renameInstrumentsMapping,
  ]);

  const workcellHasChanges =
    compareFormValues(
      initialConfig,
      defaultWorkcellConfig,
      driversByInstrumentId
    ) ||
    selectedTransportConfigId !== getWorkcellData?.workcellTransportConfigId;

  return (
    <Box padding="1rem" sx={[toolbarStyles, { width: '30rem' }]}>
      <Box>
        <SemicontrolledInput
          TextFieldProps={{ fullWidth: true, label: 'Description' }}
          value={description}
          handleSaveValue={onEditDescription}
        />
      </Box>
      <Divider sx={dividerStyles} />
      {!metadata && (
        <>
          <WarningCard message="Workflow is not yet saved" />
          <Divider sx={dividerStyles} />
        </>
      )}
      <Box>
        {getMetadataFields(metadata).map(({ label, value }) => (
          <Typography key={label} variant="body1">
            {label} {value}
          </Typography>
        ))}
      </Box>
      <Divider sx={dividerStyles} />
      <Box>
        <SchedulerVersionSelector />
      </Box>
      {isWorkcellEditToolEnabled && (
        <Box>
          <Divider sx={dividerStyles} />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box
              alignItems="center"
              display="flex"
              gap={1}
              sx={{
                width: '100%',
                justifyContent: 'space-between',
                marginRight: 2,
              }}
            >
              WORKCELL: {selectedWorkcellName}
              {workcellHasChanges && !isInteroperabilityWorkcell && (
                <Typography color="error" fontSize={14}>
                  ●
                </Typography>
              )}
            </Box>
            <EditButton
              onClick={toggleWorkcellFormModal}
              disabled={isInteroperabilityWorkcell}
            />
          </Box>
          {!isWorkcellLoading && (
            <WorkcellFormModal
              key={showWorkcellFormModal.toString()}
              defaultWorkcellConfig={defaultWorkcellConfig}
              defaultTransportConfigId={
                getWorkcellData?.workcellTransportConfigId
              }
              initialConfig={initialConfig}
              open={showWorkcellFormModal}
              workcellName={selectedWorkcellName}
              onClose={toggleWorkcellFormModal}
            />
          )}
        </Box>
      )}
      {isExecutorOptionsEnabled && (
        <Box>
          <Divider sx={dividerStyles} />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            Executor options
            <EditButton onClick={toggleExecutorOptionsModal} />
          </Box>
          {showExecutorOptionsModal && (
            <EditExecutorOptionsModal onClose={toggleExecutorOptionsModal} />
          )}
        </Box>
      )}
    </Box>
  );
};

export const ConnectedWorkflowDescriptionPanel: FC = () => {
  const dispatch = useAppDispatch();
  const description = useAppSelector(selectWorkflowDescription);
  const metadata = useAppSelector(selectWorkflowMetadata);

  const handleEditDescription = useCallback(
    (description: string) => {
      dispatch(setWorkflowDescription(description));
    },
    [dispatch]
  );

  return (
    <WorkflowDescriptionPanel
      description={description}
      metadata={metadata}
      onEditDescription={handleEditDescription}
    />
  );
};
